import React from "react";
import type { CompanyLocation } from "@mysnowscape/snow-lib";

import ChevronDownIcon from "../../../../../../resources/route-builder/chevron-down.svg";
import type { RouteEndpointLocation } from "../../../../../../types/routeBuilder";

const NEW_ADDRESS = "new-address";
const CURRENT = "current";

type LocationSelectProps = {
  label: string;
  locations: CompanyLocation[];
  selected: RouteEndpointLocation | null | undefined;
  /** Show "+ New address" as the chosen option while its form is open. */
  addingAddress?: boolean;
  disabled?: boolean;
  compact?: boolean;
  testId?: string;
  onSelect: (location: CompanyLocation) => void;
  onNewAddress: () => void;
};

function locationId(location: { _id?: unknown } | null | undefined): string {
  if (location?._id == null) return "";
  return String(location._id);
}

/** Saved company locations plus "+ New address" in one dropdown. */
export default function LocationSelect({
  label,
  locations,
  selected,
  addingAddress = false,
  disabled = false,
  compact = false,
  testId = "location-select",
  onSelect,
  onNewAddress,
}: LocationSelectProps) {
  const selectedId = locationId(selected);
  const inList =
    selectedId !== "" && locations.some((l) => locationId(l) === selectedId);
  // A one-off address picked earlier is not in the saved list; keep showing it.
  const currentLabel =
    !inList && selected?.location
      ? selected.name || selected.address || "Saved location"
      : null;
  const value = addingAddress
    ? NEW_ADDRESS
    : inList
      ? selectedId
      : currentLabel
        ? CURRENT
        : "";

  return (
    <div className="relative min-w-0 flex-1">
      <select
        aria-label={label}
        disabled={disabled}
        data-testid={testId}
        className={`block w-full appearance-none rounded-lg border border-slate-300 bg-white text-slate-800 ${
          compact ? "py-1 pl-2 pr-7 text-xs" : "py-2 pl-2 pr-8 text-sm"
        }`}
        value={value}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          if (event.target.value === NEW_ADDRESS) {
            onNewAddress();
            return;
          }
          const next = locations.find(
            (location) => locationId(location) === event.target.value,
          );
          if (next) onSelect(next);
        }}
      >
        <option value="" disabled>
          Choose location
        </option>
        {currentLabel ? (
          <option value={CURRENT} disabled>
            {currentLabel}
          </option>
        ) : null}
        {locations.map((location) => {
          const id = locationId(location);
          return (
            <option key={id || location.name} value={id}>
              {location.primary ? `${location.name} (Primary)` : location.name}
            </option>
          );
        })}
        <option value={NEW_ADDRESS}>+ New address</option>
      </select>
      <img
        src={ChevronDownIcon}
        alt=""
        aria-hidden
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${
          compact ? "right-1.5 h-3.5 w-3.5" : "right-2 h-4 w-4"
        }`}
      />
    </div>
  );
}
